/**
 * schemas.ts — zod contracts for every gateway response the SDK parses.
 *
 * Mirrors:
 *   - gateway/src/routes/catalog.ts  → `ClientCatalogResponse`
 *   - gateway/src/routes/tasks.ts    → create/get/list task responses
 *   - gateway/src/routes/usage.ts    → `UsageResponse` (BILLED-ONLY client view)
 *
 * Deliberately permissive at the edges (`.passthrough()` on the top-level
 * objects, optional/nullable on fields that older gateways may omit) so a
 * gateway that gains a field does not break a pinned consumer.
 */
import { z } from 'zod';
// ---------------------------------------------------------------------------
// Catalog — GET /v1/catalog
// ---------------------------------------------------------------------------
/** Price entry — token-metered ($/1M) or per-image. Client coefficient applied. */
export const catalogPriceSchema = z.union([
    z.object({
        kind: z.literal('token'),
        currency: z.string(),
        input_per_1m: z.number(),
        output_per_1m: z.number(),
    }),
    z.object({
        kind: z.literal('image'),
        currency: z.string(),
    }),
]);
/**
 * Client-visible model category (catalog v2).
 *
 * Not a closed enum on the wire: the gateway may add categories (video,
 * speech, …) without a client release, so an unknown value passes through as a
 * plain string rather than failing the parse. Use `CATALOG_CATEGORIES` when you
 * need to branch on the known set.
 */
export const CATALOG_CATEGORIES = ['text', 'image', 'upscaler'];
export const catalogModelSchema = z.object({
    id: z.string(),
    display_name: z.string(),
    category: z.string(),
    price: catalogPriceSchema.nullable().optional().default(null),
});
/**
 * `GET /v1/catalog` — CONTRACT v2 (idea 2026-07-19-catalog-v2-no-providers).
 *
 * A FLAT model list. There is no `providers` wrapper and no runner/provider
 * information: which engine serves a model, and what it falls back to, is
 * dotclaude-internal. A category may legitimately have zero models (today:
 * `upscaler`), so never assume a category is populated.
 */
export const catalogSchema = z.object({
    models: z.array(catalogModelSchema),
});
// ---------------------------------------------------------------------------
// Tasks — POST /v1/tasks, GET /v1/tasks/:id
// ---------------------------------------------------------------------------
/**
 * Statuses a task row can actually HAVE, and the only values
 * `GET /v1/tasks?status=` accepts — mirrors `gateway/src/schemas/tasks.ts`
 * `TASK_STATUSES`. The initial state is `ready`, NOT `queued`.
 */
export const TASK_STATUSES = ['ready', 'running', 'complete', 'failed', 'cancelled'];
/**
 * `POST /v1/tasks` reports the freshly created task as `queued` in its 201
 * body, even though the stored row is `ready` (gateway/src/routes/tasks.ts).
 * That literal therefore exists only on the create response — never filter or
 * label a fetched task with it.
 */
export const CREATED_TASK_STATUS = 'queued';
/** Terminal states — poll stops here. */
export const TERMINAL_TASK_STATUSES = new Set([
    'complete',
    'failed',
    'cancelled',
]);
export const taskCreatedSchema = z
    .object({
    task_id: z.string(),
    status: z.string(),
    created_at: z.string().optional(),
})
    .passthrough();
/**
 * Usage block on a task. The `cc` runner returns Anthropic-shaped counters;
 * older builds wrote `{ input, output }`. Both are accepted and normalized by
 * `extractTokens()`.
 */
export const taskTokensSchema = z
    .object({
    input_tokens: z.number().optional(),
    output_tokens: z.number().optional(),
    cache_creation_input_tokens: z.number().optional(),
    cache_read_input_tokens: z.number().optional(),
    input: z.number().optional(),
    output: z.number().optional(),
})
    .passthrough();
export const taskSchema = z
    .object({
    task_id: z.string(),
    status: z.string(),
    output: z.string().nullable().optional(),
    error: z.string().nullable().optional(),
    cost_usd: z.number().nullable().optional(),
    billed_usd: z.number().nullable().optional(),
    duration_ms: z.number().nullable().optional(),
    tokens: taskTokensSchema.nullable().optional(),
    meta: z.record(z.unknown()).nullable().optional(),
    created_at: z.string().optional(),
    updated_at: z.string().optional(),
})
    .passthrough();
export const taskListSchema = z
    .object({
    tasks: z.array(taskSchema),
    next_cursor: z.string().nullable().optional(),
})
    .passthrough();
// ---------------------------------------------------------------------------
// Usage — GET /v1/usage
// ---------------------------------------------------------------------------
const usageBucketBase = {
    tasks: z.number(),
    cost_usd: z.number().nullable().optional(),
    /** Client price. `null` on gateways predating billing — fall back to cost_usd. */
    billed_usd: z.number().nullable().optional(),
    input_tokens: z.number().nullable().optional(),
    output_tokens: z.number().nullable().optional(),
};
export const usageRunnerBucketSchema = z
    .object({ runner: z.string().nullable(), ...usageBucketBase })
    .passthrough();
export const usageModelBucketSchema = z
    .object({ model: z.string().nullable(), ...usageBucketBase })
    .passthrough();
export const usageSeriesPointSchema = z
    .object({ day: z.string(), ...usageBucketBase })
    .passthrough();
export const usageTotalsSchema = z.object(usageBucketBase).passthrough();
export const usageReportSchema = z
    .object({
    key: z
        .object({
        name: z.string().nullable(),
        prefix: z.string(),
        rate_limit: z.number().nullable().optional(),
        // allow_negative_balance is INTERNAL credit state and was removed from
        // the response (idea 2026-07-19-catalog-v2-no-providers). Not typed
        // here; `.passthrough()` still tolerates an older gateway sending it.
    })
        .passthrough(),
    usage: z
        .object({
        window: z
            .object({
            from: z.string(),
            to: z.string(),
            granularity: z.string(),
        })
            .passthrough(),
        total: usageTotalsSchema,
        by_runner: z.array(usageRunnerBucketSchema).default([]),
        by_model: z.array(usageModelBucketSchema).default([]),
        series: z.array(usageSeriesPointSchema).default([]),
    })
        .passthrough(),
    balance: z
        .object({
        topped_up_usd: z.number().nullable().optional(),
        charged_usd: z.number().nullable().optional(),
        balance_usd: z.number().nullable().optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
